export interface TableLayoutElements {
  viewport: HTMLElement | null;
  contentTable: HTMLTableElement | null;
  stickyHeaderTable: HTMLTableElement | null;
  collapseAllOverlay: HTMLElement | null;
  frame: HTMLElement | null;
  interactiveHead: HTMLTableSectionElement | null;
}

export interface TableLayoutMode {
  documentStickyHeader: boolean;
  floatingCollapseAll: boolean;
  clampVerticalOverscroll: boolean;
}

export interface TableOverflowState {
  start: boolean;
  end: boolean;
  scrollable: boolean;
}

export interface TableLayoutControllerOptions {
  elements: () => TableLayoutElements;
  mode: () => TableLayoutMode;
  overflowChanged: (state: TableOverflowState) => void;
  verticalEdgeWheel?: (deltaY: number) => boolean;
}

export interface TableLayoutMetricInput {
  viewportInlineSize: number;
  viewportBlockSize: number;
  scrollInlineSize: number;
  scrollBlockSize: number;
  scrollInlineOffset: number;
  collapseHeadBlockStart?: number;
  collapseFrameBlockStart?: number;
}

export interface TableLayoutMetrics {
  visibleInlineSize: number;
  overflow: TableOverflowState;
  inlineOffset: number;
  maxInlineOffset: number;
  collapseBlockOffset: number | null;
}

/** Pure geometry resolution shared by the controller and focused tests. */
export function resolveTableLayoutMetrics(input: TableLayoutMetricInput): TableLayoutMetrics {
  const horizontalOverflow = input.scrollInlineSize - input.viewportInlineSize > 1;
  return {
    visibleInlineSize: input.viewportInlineSize,
    overflow: {
      start: horizontalOverflow && input.scrollInlineOffset > 1,
      end: horizontalOverflow &&
        input.scrollInlineOffset + input.viewportInlineSize < input.scrollInlineSize - 1,
      scrollable: horizontalOverflow || input.scrollBlockSize - input.viewportBlockSize > 1,
    },
    inlineOffset: input.scrollInlineOffset,
    maxInlineOffset: Math.max(0, input.scrollInlineSize - input.viewportInlineSize),
    collapseBlockOffset: input.collapseHeadBlockStart == null ||
      input.collapseFrameBlockStart == null
      ? null
      : Math.max(0, input.collapseHeadBlockStart - input.collapseFrameBlockStart),
  };
}

/**
 * Owns table viewport observation. It resolves newly rendered geometry
 * immediately so the first paint has the correct viewport-width chrome, then
 * batches observer-driven scroll and resize reads/writes into one animation frame.
 * The component remains responsible only for refs and reactive overflow state.
 */
export class TableLayoutController {
  private connected = false;
  private connectedViewport: HTMLElement | null = null;
  private connectedContentTable: HTMLTableElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private frame: number | null = null;
  private overflow: TableOverflowState | null = null;
  private inlineOffset = 0;
  private maxInlineOffset = 0;
  private maxBlockOffset = 0;
  private lastMode: TableLayoutMode | null = null;

  constructor(private readonly options: TableLayoutControllerOptions) {}

  connect(): void {
    this.connected = true;
    this.refresh();
  }

  disconnect(): void {
    this.connected = false;
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.connectedViewport?.removeEventListener('scroll', this.handleScroll);
    this.connectedViewport?.removeEventListener('wheel', this.handleWheel);
    this.connectedViewport = null;
    this.connectedContentTable = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  /** Reconcile newly rendered refs, observers, and pending geometry. */
  refresh(forceGeometry = true): void {
    if (!this.connected) return;
    const { viewport, contentTable } = this.options.elements();
    const elementsChanged = viewport !== this.connectedViewport ||
      contentTable !== this.connectedContentTable;
    if (elementsChanged) {
      this.connectedViewport?.removeEventListener('scroll', this.handleScroll);
      this.connectedViewport?.removeEventListener('wheel', this.handleWheel);
      this.resizeObserver?.disconnect();
      this.connectedViewport = viewport;
      this.connectedContentTable = contentTable;
      viewport?.addEventListener('scroll', this.handleScroll, { passive: true });
      viewport?.addEventListener('wheel', this.handleWheel, { passive: false });

      if (typeof ResizeObserver !== 'undefined' && viewport) {
        this.resizeObserver ??= new ResizeObserver(this.schedule);
        this.resizeObserver.observe(viewport);
        if (contentTable) this.resizeObserver.observe(contentTable);
      }
    }
    const mode = this.options.mode();
    const modeChanged = !this.lastMode ||
      mode.documentStickyHeader !== this.lastMode.documentStickyHeader ||
      mode.floatingCollapseAll !== this.lastMode.floatingCollapseAll ||
      mode.clampVerticalOverscroll !== this.lastMode.clampVerticalOverscroll;
    this.lastMode = mode;
    // New refs and explicit refreshes resolve before paint. A recycled virtual
    // window keeps the same scroll geometry, so ResizeObserver owns later size
    // changes without forcing layout after every row patch.
    if (forceGeometry || elementsChanged || modeChanged) this.sync();
  }

  private schedule = (): void => {
    if (!this.connected || this.frame !== null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.sync();
    });
  };

  private readonly handleScroll = (): void => {
    const viewport = this.connectedViewport;
    if (!viewport) return;
    const mode = this.options.mode();
    this.clampInlineOffset();
    if (mode.clampVerticalOverscroll) this.clampBlockOffset();
    if (
      viewport.scrollLeft !== this.inlineOffset ||
      (mode.floatingCollapseAll && !mode.documentStickyHeader)
    ) {
      this.schedule();
    }
  };

  private readonly handleWheel = (event: WheelEvent): void => {
    const viewport = this.connectedViewport;
    if (!viewport) return;

    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      const atStart = viewport.scrollLeft <= 0 && event.deltaX < 0;
      const atEnd = viewport.scrollLeft >= this.maxInlineOffset && event.deltaX > 0;
      if (atStart || atEnd) event.preventDefault();
      return;
    }

    if (!this.options.mode().clampVerticalOverscroll || event.deltaY === 0) return;
    const atStart = viewport.scrollTop <= 0 && event.deltaY < 0;
    const atEnd = viewport.scrollTop >= this.maxBlockOffset && event.deltaY > 0;
    if (!atStart && !atEnd) return;

    this.options.verticalEdgeWheel?.(event.deltaY);
    event.preventDefault();
  };

  private clampInlineOffset(): void {
    const viewport = this.connectedViewport;
    if (!viewport) return;
    const clamped = Math.min(this.maxInlineOffset, Math.max(0, viewport.scrollLeft));
    if (viewport.scrollLeft !== clamped) viewport.scrollLeft = clamped;
  }

  private clampBlockOffset(): void {
    const viewport = this.connectedViewport;
    if (!viewport) return;
    const clamped = Math.min(this.maxBlockOffset, Math.max(0, viewport.scrollTop));
    if (viewport.scrollTop !== clamped) viewport.scrollTop = clamped;
  }

  private sync(): void {
    const elements = this.options.elements();
    const viewport = elements.viewport;
    if (!viewport) return;

    // Read every layout value before writing styles or reactive state.
    const mode = this.options.mode();
    const measureCollapse = mode.floatingCollapseAll && !mode.documentStickyHeader &&
      elements.collapseAllOverlay && elements.frame && elements.interactiveHead;
    const metrics = resolveTableLayoutMetrics({
      viewportInlineSize: viewport.clientWidth,
      viewportBlockSize: viewport.clientHeight,
      scrollInlineSize: viewport.scrollWidth,
      scrollBlockSize: viewport.scrollHeight,
      scrollInlineOffset: viewport.scrollLeft,
      collapseHeadBlockStart: measureCollapse
        ? elements.interactiveHead!.getBoundingClientRect().top
        : undefined,
      collapseFrameBlockStart: measureCollapse
        ? elements.frame!.getBoundingClientRect().top
        : undefined,
    });
    this.inlineOffset = metrics.inlineOffset;
    this.maxInlineOffset = metrics.maxInlineOffset;
    this.maxBlockOffset = Math.max(0, viewport.scrollHeight - viewport.clientHeight);

    this.setProperty(
      viewport,
      '--ds-table-visible-inline-size',
      `${metrics.visibleInlineSize}px`,
    );
    if (elements.stickyHeaderTable) {
      this.setProperty(
        elements.stickyHeaderTable,
        '--ds-table-inline-scroll-offset',
        `${metrics.inlineOffset}px`,
      );
      this.setProperty(
        elements.stickyHeaderTable,
        '--ds-table-inline-scroll-max-offset',
        `${metrics.maxInlineOffset}px`,
      );
    }
    if (metrics.collapseBlockOffset !== null && elements.collapseAllOverlay) {
      this.setProperty(
        elements.collapseAllOverlay,
        '--ds-table-collapse-all-block-offset',
        `${metrics.collapseBlockOffset}px`,
      );
    }

    if (
      !this.overflow ||
      this.overflow.start !== metrics.overflow.start ||
      this.overflow.end !== metrics.overflow.end ||
      this.overflow.scrollable !== metrics.overflow.scrollable
    ) {
      this.overflow = metrics.overflow;
      this.options.overflowChanged(metrics.overflow);
    }
  }

  private setProperty(element: HTMLElement, property: string, value: string): void {
    if (element.style.getPropertyValue(property) !== value) {
      element.style.setProperty(property, value);
    }
  }
}
